import {
  lazy,
  Suspense,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import MCPFooter from './MCPFooter';
import MCP from './MCP';
import ConsentForm from '../../Flow/ConsentForm';
import { McpContextProvider } from './McpContextProvider';
import { useToken } from '../../Flow/ConsentContextProvider';
import { classifyScopes } from '../MCP/domain/trelloScopes';

/**
 * Lazily imported so the AGG/Relay `graphql` tag it pulls in is only evaluated
 * when a Trello token actually renders it. Importing it eagerly — as the default
 * flow's layout does — would put Relay in the import graph of everything that
 * reaches this module, including consent flows that never touch Trello and the
 * unit tests covering them.
 *
 * The Suspense boundary below already exists for the query itself, so it covers
 * the chunk load too and there is no extra fallback state to design.
 */
const TrelloWorkspaceFetcher = lazy(() => import('./TrelloWorkspaceFetcher'));

const mcpConsentMainStyle = xcss({
  backgroundColor: 'elevation.surface',
  boxSizing: 'border-box',
  padding: 'space.0',
  flex: '1 1 0%',
  minWidth: '0',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const mcpMainViewportStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const mcpFormFlexStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const contentGrowStyle = xcss({
  flex: '0 1 auto',
  minWidth: '0',
  display: 'flex',
  flexDirection: 'column',
});

const ContentGrowWrapper: FunctionComponent<PropsWithChildren> = ({
  children,
}) => <Box xcss={contentGrowStyle}>{children}</Box>;

/** Inner layout: the consent form, its content, and the fixed footer. */
const McpConsentInner: FunctionComponent = () => {
  return (
    <Box as="main" xcss={[mcpConsentMainStyle, mcpMainViewportStyle]}>
      <Box xcss={mcpFormFlexStyle}>
        <ConsentForm>
          <ContentGrowWrapper>
            <MCP />
          </ContentGrowWrapper>
          <MCPFooter />
        </ConsentForm>
      </Box>
    </Box>
  );
};

const McpConsentLayout: FunctionComponent = () => {
  const token = useToken();
  const { hasTrello } = classifyScopes(token.scopes ?? []);

  if (hasTrello) {
    return (
      <Suspense
        fallback={
          <McpContextProvider trelloWorkspacesLoading>
            <McpConsentInner />
          </McpContextProvider>
        }
      >
        <TrelloWorkspaceFetcher>
          <McpConsentInner />
        </TrelloWorkspaceFetcher>
      </Suspense>
    );
  }

  return (
    <McpContextProvider>
      <McpConsentInner />
    </McpContextProvider>
  );
};

export default McpConsentLayout;
